




















































import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';

// Shared types
import {
    AdditionalProduct,
    ServiceProduct,
    PriceType,
    QuantityUnit,
} from '@coldtec/cos-types';

// Store types
import {
    ProductsAction,
    ProductType,
    ToolProduct,
    RequestAction,
} from '@/store/store.types';

/**
 *  The footer of a product, including the price, amount logic and
 *  a button to add or remove the product from the request.
 */
@Component
export default class ProductFooter extends Vue {

    @Prop({ type: Object, required: true })
    private product!: ToolProduct;

    /**
     *  Holding the original price of the product for automatically
     *  computing the price when the amount changes.
     */
    private originalPrice = 0;

    /**
     *  Holding a note about the price of the product.
     */
    private note: string | null = null;

    /**
     *  Holding the (translated) frequency of the price.
     */
    private frequency: string | null = null;

    /**
     *  Holding the fact if the user should be able
     *  to adjust the amount and price.
     */
    private adjustableAmount = false;

    /**
     *  Holding the quantity unit of the product.
     */
    private quantityUnit = '';

    /**
     *
     *  Used to get the price of the product.
     *
     *  (i) Dividing it by 100 in case a user
     *
     */
    private get price(): number {

        if (!this.product.price) {

            return 0;

        }

        return this.adjustableAmount
            ? this.product.price / 100
            : this.product.price;

    }

    /**
     *  Used to update the price of the product and dispatching
     *  a store action to sync the request products.
     */
    private set price(cents: number) {

        this.product.price = cents * 100;

        /**
         *  Dispatch an action to sync the request products.
         */
        this.$store.dispatch(RequestAction.SYNC_PRODUCTS);

    }

    /**
     *  Holding the translations of every price type / frequency.
     */
    private priceTypes: { [type: string]: string } = {
        [PriceType.Monthly]: 'per maand',
        [PriceType.Yearly]: 'per jaar',
    }

    /**
     *  Holding the labels of the quantity units.
     */
    private quantityUnitLabels: { [type: string]: string } = {
        [QuantityUnit.Piece]: 'stk.',
        [QuantityUnit.Meter]: 'mtr.',
    }

    /**
     *  Used to get the title for the toggle button of the product.
     */
    private get buttonTitle(): string {

        return this.product.selected
            ? 'Verwijder dit product uit je aanvraag'
            : 'Voeg dit product toe aan je aanvraag';

    }

    /**
     *  Used to get the content for the toggle button of the product.
     */
    private get buttonContent(): string {

        return this.product.selected
            ? 'Verwijderen'
            : 'Toevoegen';

    }

    /**
     *  Get the data of the product and construct
     *  the price and optional note and frequency.
     */
    private created(): void {

        let productData: AdditionalProduct | ServiceProduct;

        switch (this.product.type) {

            case ProductType.Additional:

                productData = this._getProductData<AdditionalProduct>();

                /**
                 *  The amount and price must be adjustable of every
                 *  additional product (that is returned by the API).
                 */
                this.adjustableAmount = this.$store.state.internal;

                /**
                 *  Set the correct quantity unit.
                 */
                this.quantityUnit = this.quantityUnitLabels[productData.quantity_unit];

                break;

            case ProductType.Service:

                productData = this._getProductData<ServiceProduct>();

                /**
                 *  Only a service product contains a note about the price.
                 */
                this.note = productData.note;

                /**
                 *  Only show a frequency if it's per month or per year.
                 */
                if (productData.type && productData.type !== PriceType.OneTime) {

                    this.frequency = this.priceTypes[productData.type];

                }

                break;

            default:

                throw new Error(`Got unknown type of product "${ this.product.type }"`);

        }

        /**
         *  Store the original price to be able to compute
         *  the price when the amount changes.
         */
        this.originalPrice = this.product.price || 0;

    }

    /**
     *  Used to compute the price if the amount changes.
     */
    private computePrice(): void {

        this.price = (this.product.amount * this.originalPrice) / 100;

    }

    /**
     *  Used to dispatch the action for toggling the selected state of a product.
     */
    private toggleProduct(): void {

        this.$store.dispatch(ProductsAction.TOGGLE_SELECTED, this.product.guid);

    }

    /**
     *  Used to get the (correctly typed) data of the product.
     */
    private _getProductData<Type>(): Type {

        return this.product.data as unknown as Type;

    }

}
